import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminCategories } from '@/hooks/useAdminData';
import { createProduct } from '@/data/adminProducts';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

const AdminAddProduct = () => {
  const { isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const { data: categories = [] } = useAdminCategories();

  // Basic fields
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [serviceType, setServiceType] = useState<'product' | 'service'>('product');
  const [price, setPrice] = useState('');
  const [msrp, setMsrp] = useState('');
  const [image, setImage] = useState('');
  const [demoVideoUrl, setDemoVideoUrl] = useState('');
  const [isSelect, setIsSelect] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  // Array fields
  const [gallery, setGallery] = useState<string[]>(['']);
  const [tags, setTags] = useState<string[]>(['']);
  const [features, setFeatures] = useState<Array<{ title: string; description: string }>>([
    { title: '', description: '' },
  ]);

  // Pro discount fields
  const [proDiscountMode, setProDiscountMode] = useState<'none' | 'instant' | 'rebate' | 'vendorAdjusted'>('none');
  const [proInstantPercent, setProInstantPercent] = useState('10');
  const [proRebatePercent, setProRebatePercent] = useState('10');
  const [vendorMarginPercent, setVendorMarginPercent] = useState('20');

  // Sale fields
  const [saleEnabled, setSaleEnabled] = useState(false);
  const [salePrice, setSalePrice] = useState('');
  const [saleStartsAt, setSaleStartsAt] = useState('');
  const [saleEndsAt, setSaleEndsAt] = useState('');

  // Dynamic pricing fields
  const [dynamicEnabled, setDynamicEnabled] = useState(false);
  const [dynamicMinPrice, setDynamicMinPrice] = useState('');
  const [dynamicMaxPrice, setDynamicMaxPrice] = useState('');
  const [dynamicStrategy, setDynamicStrategy] = useState<'steady' | 'volume_push' | 'clearance' | 'pro_day'>('steady');

  // Service-specific fields
  const [roiPercentage, setRoiPercentage] = useState('');
  const [roiTime, setRoiTime] = useState('');
  const [roiMetrics, setRoiMetrics] = useState<Array<{ label: string; value: string }>>([
    { label: '', value: '' },
  ]);
  const [setupDuration, setSetupDuration] = useState('');
  const [setupSteps, setSetupSteps] = useState<string[]>(['']);
  const [testimonialVideos, setTestimonialVideos] = useState<
    Array<{ name: string; role: string; video_url: string; quote: string }>
  >([{ name: '', role: '', video_url: '', quote: '' }]);

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/');
    }
  }, [isAdmin, loading, navigate]);

  // Auto-generate slug from title
  useEffect(() => {
    if (title && !slug) {
      const autoSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
      setSlug(autoSlug);
    }
  }, [title, slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Validate required fields
      if (!title || !slug || selectedCategories.length === 0 || !price || !msrp) {
        toast.error('Please fill in all required fields');
        setSubmitting(false);
        return;
      }

      // Prepare data
      const productData = {
        title,
        subtitle: subtitle || null,
        description: description || null,
        slug,
        categories: selectedCategories,
        service_type: serviceType,
        price: parseFloat(price),
        msrp: parseFloat(msrp),
        image: image || null,
        demo_video_url: demoVideoUrl || null,
        is_select: isSelect,
        is_published: isPublished,
        gallery: gallery.filter((url) => url.trim() !== ''),
        tags: tags.filter((tag) => tag.trim() !== ''),
        features: features.filter((f) => f.title.trim() !== '' || f.description.trim() !== ''),
        roi_metrics:
          serviceType === 'service' && (roiPercentage || roiTime)
            ? {
                percentage: roiPercentage || null,
                time: roiTime || null,
                metrics: roiMetrics.filter((m) => m.label.trim() !== '' && m.value.trim() !== ''),
              }
            : null,
        setup_info:
          serviceType === 'service' && setupDuration
            ? {
                duration: setupDuration,
                steps: setupSteps.filter((s) => s.trim() !== ''),
              }
            : null,
        testimonial_videos:
          serviceType === 'service'
            ? testimonialVideos.filter((t) => t.name.trim() !== '' || t.video_url.trim() !== '')
            : null,
        // Pro discount settings
        pro_discount_mode: proDiscountMode,
        pro_instant_percent: (proDiscountMode === 'instant' || proDiscountMode === 'vendorAdjusted') 
          ? parseFloat(proInstantPercent) 
          : null,
        pro_rebate_percent: proDiscountMode === 'rebate' 
          ? parseFloat(proRebatePercent) 
          : null,
        vendor_margin_percent: parseFloat(vendorMarginPercent) || null,
        // Sale settings
        sale_enabled: saleEnabled,
        sale_price: saleEnabled && salePrice ? parseFloat(salePrice) : null,
        sale_starts_at: saleEnabled && saleStartsAt ? new Date(saleStartsAt).toISOString() : null,
        sale_ends_at: saleEnabled && saleEndsAt ? new Date(saleEndsAt).toISOString() : null,
        // Dynamic pricing settings
        dynamic_enabled: dynamicEnabled,
        dynamic_min_price: dynamicEnabled && dynamicMinPrice ? parseFloat(dynamicMinPrice) : null,
        dynamic_max_price: dynamicEnabled && dynamicMaxPrice ? parseFloat(dynamicMaxPrice) : null,
        dynamic_strategy: dynamicEnabled ? dynamicStrategy : null,
        current_price: null,
      };

      await createProduct(productData);

      toast.success('Product created successfully!');
      navigate('/admin');
    } catch (error: any) {
      console.error('Error creating product:', error);
      toast.error(error.message || 'Failed to create product');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>

          <Card>
            <CardHeader>
              <CardTitle>Add New Product</CardTitle>
              <CardDescription>Create a new product or service listing</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">
                        Title <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Product name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="slug">
                        Slug <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="slug"
                        value={slug}
                        onChange={(e) => setSlug(e.target.value)}
                        placeholder="product-slug"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subtitle">Subtitle</Label>
                    <Input
                      id="subtitle"
                      value={subtitle}
                      onChange={(e) => setSubtitle(e.target.value)}
                      placeholder="Short tagline"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Detailed product description"
                      rows={4}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="categories">
                        Categories <span className="text-destructive">*</span>
                      </Label>
                      <div className="border rounded-md p-3 min-h-[80px]">
                        <div className="flex flex-wrap gap-2 mb-2">
                          {selectedCategories.map((catSlug) => {
                            const cat = categories.find(c => c.slug === catSlug);
                            return (
                              <Badge key={catSlug} variant="secondary" className="gap-1">
                                {cat?.name || catSlug}
                                <X 
                                  className="h-3 w-3 cursor-pointer hover:text-destructive" 
                                  onClick={() => setSelectedCategories(prev => prev.filter(c => c !== catSlug))}
                                />
                              </Badge>
                            );
                          })}
                        </div>
                        <Select 
                          value="" 
                          onValueChange={(value) => {
                            if (!selectedCategories.includes(value)) {
                              setSelectedCategories([...selectedCategories, value]);
                            }
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Add category..." />
                          </SelectTrigger>
                          <SelectContent>
                            {categories
                              .filter(cat => !selectedCategories.includes(cat.slug))
                              .map((cat) => (
                                <SelectItem key={cat.slug} value={cat.slug}>
                                  {cat.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="serviceType">Type</Label>
                      <Select
                        value={serviceType}
                        onValueChange={(v) => setServiceType(v as 'product' | 'service')}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="product">Product</SelectItem>
                          <SelectItem value="service">Service</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Pricing */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Pricing</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="price">
                        Price <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="99.99"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="msrp">
                        MSRP <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="msrp"
                        type="number"
                        step="0.01"
                        value={msrp}
                        onChange={(e) => setMsrp(e.target.value)}
                        placeholder="199.99"
                        required
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Circle Pro & Sales Settings */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Circle Pro & Sales Settings</h3>
                  
                  {/* Pro Discount Mode */}
                  <div className="space-y-2">
                    <Label htmlFor="proDiscountMode">Pro Member Discount Type</Label>
                    <Select value={proDiscountMode} onValueChange={(v) => setProDiscountMode(v as any)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Pro discount</SelectItem>
                        <SelectItem value="instant">Instant discount at checkout</SelectItem>
                        <SelectItem value="rebate">Rebate after purchase</SelectItem>
                        <SelectItem value="vendorAdjusted">Vendor-adjusted price + Pro discount</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Choose how Pro members benefit on this product
                    </p>
                  </div>

                  {/* Instant Discount Fields */}
                  {(proDiscountMode === 'instant' || proDiscountMode === 'vendorAdjusted') && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="proInstantPercent">Pro Instant Discount %</Label>
                        <Input
                          id="proInstantPercent"
                          type="number"
                          min="0"
                          max="50"
                          step="1"
                          value={proInstantPercent}
                          onChange={(e) => setProInstantPercent(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">Default: 10%</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vendorMarginPercent">Vendor Margin %</Label>
                        <Input
                          id="vendorMarginPercent"
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={vendorMarginPercent}
                          onChange={(e) => setVendorMarginPercent(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">Most vendors give 20%+ margin</p>
                      </div>
                    </div>
                  )}

                  {/* Rebate Fields */}
                  {proDiscountMode === 'rebate' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="proRebatePercent">Pro Rebate %</Label>
                        <Input
                          id="proRebatePercent"
                          type="number"
                          min="0"
                          max="50"
                          step="1"
                          value={proRebatePercent}
                          onChange={(e) => setProRebatePercent(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">Default: 10%</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vendorMarginPercentRebate">Vendor Margin %</Label>
                        <Input
                          id="vendorMarginPercentRebate"
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={vendorMarginPercent}
                          onChange={(e) => setVendorMarginPercent(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  <Separator className="my-4" />

                  {/* Sale Settings */}
                  <div className="space-y-4">
                    <h4 className="text-md font-semibold">Sale & Limited-Time Pricing</h4>
                    <p className="text-sm text-muted-foreground">
                      Configure limited-time sales with countdown timers
                    </p>
                    
                    <div className="flex items-center gap-2">
                      <Switch checked={saleEnabled} onCheckedChange={setSaleEnabled} />
                      <Label>Sale Enabled</Label>
                    </div>

                    {saleEnabled && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="salePrice">Sale Price</Label>
                            <Input
                              id="salePrice"
                              type="number"
                              step="0.01"
                              value={salePrice}
                              onChange={(e) => setSalePrice(e.target.value)}
                              placeholder="79.99"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="saleStartsAt">Sale Starts At</Label>
                            <Input
                              id="saleStartsAt"
                              type="datetime-local"
                              value={saleStartsAt}
                              onChange={(e) => setSaleStartsAt(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="saleEndsAt">Sale Ends At</Label>
                            <Input
                              id="saleEndsAt"
                              type="datetime-local"
                              value={saleEndsAt}
                              onChange={(e) => setSaleEndsAt(e.target.value)}
                            />
                          </div>
                        </div>

                        {saleEnabled && (!salePrice || !saleEndsAt || (saleStartsAt && saleEndsAt && new Date(saleEndsAt) <= new Date(saleStartsAt))) && (
                          <div className="bg-destructive/10 text-destructive text-sm p-3 rounded">
                            ⚠️ Sale requires: positive price, end date after start date
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <Separator className="my-4" />

                  {/* Dynamic Pricing */}
                  <div className="space-y-4">
                    <h4 className="text-md font-semibold">Dynamic Pricing Settings</h4>
                    <p className="text-sm text-muted-foreground">
                      Foundation for future Pro Day events and automated pricing (not yet active)
                    </p>
                    
                    <div className="flex items-center gap-2">
                      <Switch checked={dynamicEnabled} onCheckedChange={setDynamicEnabled} />
                      <Label>Dynamic Pricing (Coming Soon)</Label>
                    </div>

                    {dynamicEnabled && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="dynamicMinPrice">Minimum Price</Label>
                            <Input
                              id="dynamicMinPrice"
                              type="number"
                              step="0.01"
                              value={dynamicMinPrice}
                              onChange={(e) => setDynamicMinPrice(e.target.value)}
                              placeholder="49.99"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="dynamicMaxPrice">Maximum Price</Label>
                            <Input
                              id="dynamicMaxPrice"
                              type="number"
                              step="0.01"
                              value={dynamicMaxPrice}
                              onChange={(e) => setDynamicMaxPrice(e.target.value)}
                              placeholder="199.99"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="dynamicStrategy">Strategy</Label>
                            <Select value={dynamicStrategy} onValueChange={(v) => setDynamicStrategy(v as any)}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="steady">Steady pricing</SelectItem>
                                <SelectItem value="volume_push">Volume push</SelectItem>
                                <SelectItem value="clearance">Clearance</SelectItem>
                                <SelectItem value="pro_day">Pro Day special</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="bg-blue-50 text-blue-800 text-xs p-3 rounded">
                          ℹ️ Dynamic pricing engine not yet implemented. These settings will be used in future Circle Pro Day events.
                        </div>
                      </>
                    )}
                  </div>

                  <Separator className="my-4" />

                  {/* Current Price Preview */}
                  <div className="bg-muted p-4 rounded-lg">
                    <h4 className="text-sm font-semibold mb-2">Effective Base Price Preview</h4>
                    <div className="text-2xl font-bold">
                      ${(() => {
                        if (saleEnabled && salePrice && saleStartsAt && saleEndsAt) {
                          const now = new Date();
                          const start = new Date(saleStartsAt);
                          const end = new Date(saleEndsAt);
                          if (now >= start && now <= end) {
                            return parseFloat(salePrice).toFixed(2);
                          }
                        }
                        return parseFloat(price || '0').toFixed(2);
                      })()}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      This is the base price users will see right now (before Pro discount)
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Media */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Media</h3>
                  <div className="space-y-2">
                    <Label htmlFor="image">Main Image URL</Label>
                    <Input
                      id="image"
                      value={image}
                      onChange={(e) => setImage(e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="demoVideo">Demo Video URL</Label>
                    <Input
                      id="demoVideo"
                      value={demoVideoUrl}
                      onChange={(e) => setDemoVideoUrl(e.target.value)}
                      placeholder="https://youtube.com/..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Gallery URLs</Label>
                    {gallery.map((url, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={url}
                          onChange={(e) => {
                            const newGallery = [...gallery];
                            newGallery[index] = e.target.value;
                            setGallery(newGallery);
                          }}
                          placeholder="https://... or YouTube URL"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setGallery(gallery.filter((_, i) => i !== index))}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setGallery([...gallery, ''])}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Gallery Item
                    </Button>
                  </div>
                </div>

                <Separator />

                {/* Tags & Features */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Tags & Features</h3>
                  <div className="space-y-2">
                    <Label>Tags</Label>
                    {tags.map((tag, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={tag}
                          onChange={(e) => {
                            const newTags = [...tags];
                            newTags[index] = e.target.value;
                            setTags(newTags);
                          }}
                          placeholder="Tag name"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setTags(tags.filter((_, i) => i !== index))}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setTags([...tags, ''])}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Tag
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Features</Label>
                    {features.map((feature, index) => (
                      <Card key={index} className="p-4">
                        <div className="space-y-2">
                          <Input
                            value={feature.title}
                            onChange={(e) => {
                              const newFeatures = [...features];
                              newFeatures[index].title = e.target.value;
                              setFeatures(newFeatures);
                            }}
                            placeholder="Feature title"
                          />
                          <Textarea
                            value={feature.description}
                            onChange={(e) => {
                              const newFeatures = [...features];
                              newFeatures[index].description = e.target.value;
                              setFeatures(newFeatures);
                            }}
                            placeholder="Feature description"
                            rows={2}
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => setFeatures(features.filter((_, i) => i !== index))}
                          >
                            Remove Feature
                          </Button>
                        </div>
                      </Card>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFeatures([...features, { title: '', description: '' }])}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Feature
                    </Button>
                  </div>
                </div>

                {/* Service-Specific Fields */}
                {serviceType === 'service' && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Service Details</h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="roiPercentage">ROI Percentage</Label>
                          <Input
                            id="roiPercentage"
                            value={roiPercentage}
                            onChange={(e) => setRoiPercentage(e.target.value)}
                            placeholder="300%"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="roiTime">ROI Time</Label>
                          <Input
                            id="roiTime"
                            value={roiTime}
                            onChange={(e) => setRoiTime(e.target.value)}
                            placeholder="12 months"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>ROI Metrics</Label>
                        {roiMetrics.map((metric, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              value={metric.label}
                              onChange={(e) => {
                                const newMetrics = [...roiMetrics];
                                newMetrics[index].label = e.target.value;
                                setRoiMetrics(newMetrics);
                              }}
                              placeholder="Metric label"
                            />
                            <Input
                              value={metric.value}
                              onChange={(e) => {
                                const newMetrics = [...roiMetrics];
                                newMetrics[index].value = e.target.value;
                                setRoiMetrics(newMetrics);
                              }}
                              placeholder="Metric value"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => setRoiMetrics(roiMetrics.filter((_, i) => i !== index))}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setRoiMetrics([...roiMetrics, { label: '', value: '' }])}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add Metric
                        </Button>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="setupDuration">Setup Duration</Label>
                        <Input
                          id="setupDuration"
                          value={setupDuration}
                          onChange={(e) => setSetupDuration(e.target.value)}
                          placeholder="2-4 weeks"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Setup Steps</Label>
                        {setupSteps.map((step, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              value={step}
                              onChange={(e) => {
                                const newSteps = [...setupSteps];
                                newSteps[index] = e.target.value;
                                setSetupSteps(newSteps);
                              }}
                              placeholder="Setup step"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => setSetupSteps(setupSteps.filter((_, i) => i !== index))}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setSetupSteps([...setupSteps, ''])}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add Step
                        </Button>
                      </div>

                      <div className="space-y-2">
                        <Label>Testimonial Videos</Label>
                        {testimonialVideos.map((testimonial, index) => (
                          <Card key={index} className="p-4">
                            <div className="space-y-2">
                              <Input
                                value={testimonial.name}
                                onChange={(e) => {
                                  const newTestimonials = [...testimonialVideos];
                                  newTestimonials[index].name = e.target.value;
                                  setTestimonialVideos(newTestimonials);
                                }}
                                placeholder="Person name"
                              />
                              <Input
                                value={testimonial.role}
                                onChange={(e) => {
                                  const newTestimonials = [...testimonialVideos];
                                  newTestimonials[index].role = e.target.value;
                                  setTestimonialVideos(newTestimonials);
                                }}
                                placeholder="Role/Title"
                              />
                              <Input
                                value={testimonial.video_url}
                                onChange={(e) => {
                                  const newTestimonials = [...testimonialVideos];
                                  newTestimonials[index].video_url = e.target.value;
                                  setTestimonialVideos(newTestimonials);
                                }}
                                placeholder="Video URL"
                              />
                              <Textarea
                                value={testimonial.quote}
                                onChange={(e) => {
                                  const newTestimonials = [...testimonialVideos];
                                  newTestimonials[index].quote = e.target.value;
                                  setTestimonialVideos(newTestimonials);
                                }}
                                placeholder="Quote"
                                rows={2}
                              />
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() =>
                                  setTestimonialVideos(testimonialVideos.filter((_, i) => i !== index))
                                }
                              >
                                Remove Testimonial
                              </Button>
                            </div>
                          </Card>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setTestimonialVideos([
                              ...testimonialVideos,
                              { name: '', role: '', video_url: '', quote: '' },
                            ])
                          }
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add Testimonial
                        </Button>
                      </div>
                    </div>
                  </>
                )}

                <Separator />

                {/* Settings */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Settings</h3>
                  <div className="flex items-center space-x-2">
                    <Switch id="isSelect" checked={isSelect} onCheckedChange={setIsSelect} />
                    <Label htmlFor="isSelect">Circle Select Product</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch id="isPublished" checked={isPublished} onCheckedChange={setIsPublished} />
                    <Label htmlFor="isPublished">Published</Label>
                  </div>
                </div>

                <Separator />

                {/* Submit */}
                <div className="flex gap-4">
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Product'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => navigate('/admin')}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminAddProduct;
